import { motion } from "framer-motion";
import { ChevronDown, ArrowRight } from "lucide-react";

const HERO_IMG = "/hero-arc.png";
const LOGO_IMG = "/logo-full.png";

export const Hero = ({ t }) => {
  const go = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-black"
    >
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Luxusný zlatý oblúk SauberEko"
          className="w-full h-full object-cover object-[82%_center] md:object-center opacity-40 md:opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 md:via-black/70 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/30" />
      </div>

      {/* Rotujúce registrované logo — luxusný prvok v pozadí */}
      <div
        data-testid="hero-logo-rotator"
        className="absolute inset-0 z-[1] flex items-center justify-center md:justify-end md:pr-[8%] pointer-events-none select-none"
        aria-hidden="true"
      >
        <div className="relative flex items-center justify-center" style={{ perspective: "1200px" }}>
          <motion.img
            src={LOGO_IMG}
            alt=""
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1, rotateY: 360 }}
            transition={{
              opacity: { duration: 2, ease: "easeOut" },
              scale: { duration: 2, ease: "easeOut" },
              rotateY: { duration: 18, repeat: Infinity, ease: "linear" },
            }}
            className="relative z-[1] w-[clamp(260px,48vw,460px)] opacity-25 md:opacity-45"
            style={{ filter: "drop-shadow(0 0 45px rgba(212,175,55,0.5))", transformStyle: "preserve-3d" }}
          />
          {/* Krásny zlatý kruh — vpredu, logo zostáva vzadu */}
          <div className="pointer-events-none absolute z-[2] aspect-square w-[clamp(340px,60vw,580px)] rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.12),transparent_62%)]">
            <div className="absolute inset-0 rounded-full border border-[#D4AF37]/50 shadow-[0_0_70px_rgba(212,175,55,0.3),inset_0_0_50px_rgba(212,175,55,0.12)]" />
            <div className="absolute inset-[7%] rounded-full border border-[#D4AF37]/25" />
            <div className="absolute inset-[14%] rounded-full border border-[#D4AF37]/10" />
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pt-28 pb-20">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex items-center gap-3 text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-8"
          >
            <span className="h-px w-10 bg-[#D4AF37]/60" />
            {t.hero.badge}
          </motion.div>

          <motion.h1
            data-testid="hero-heading"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.1 }}
            className="font-display font-semibold leading-[0.95] text-6xl md:text-8xl tracking-tight"
          >
            Sauber<span className="gold-gradient-text">Eko</span>
            <sup className="text-[#D4AF37] text-2xl md:text-3xl align-super">®</sup>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="font-display italic text-2xl md:text-4xl text-white/90 mt-6"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.45 }}
            className="text-base md:text-lg text-white/65 leading-relaxed mt-8 max-w-2xl"
          >
            {t.hero.paragraph}
          </motion.p>

          {t.hero.heroQuote && (
            <motion.p
              data-testid="hero-quote"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.5 }}
              className="mt-8 text-[#D4AF37]/80 text-sm md:text-base italic font-display leading-relaxed max-w-xl whitespace-pre-line"
            >
              {t.hero.heroQuote}
              <span data-testid="hero-quote-author" className="block not-italic text-white/40 text-xs mt-2 tracking-wide">
                © Martin Smrkta, {t.hero.heroQuoteAuthor} SauberEko<sup>®</sup>
              </span>
            </motion.p>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 mt-8"
          >
            <button
              data-testid="hero-cta"
              onClick={() => go("contact")}
              className="group inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-semibold px-8 py-4 hover:bg-[#F2E3C6] transition-colors duration-300"
            >
              {t.hero.ctaPrimary}
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform duration-300"
              />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => go("standard")}
              className="inline-flex items-center justify-center border border-[#D4AF37]/60 text-[#D4AF37] px-8 py-4 hover:bg-[#D4AF37]/10 transition-colors duration-300"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/40">
        <span className="text-[10px] tracking-[0.3em] uppercase">
          {t.hero.scroll}
        </span>
        <ChevronDown size={18} className="animate-bounce" />
      </div>
    </section>
  );
};
